/**
 * Merge/Upgrade-System (docs/02, A6 vereinfacht): 3x gleich -> naechstes Tier.
 * Champions: pauschale Stat-Skalierung, KEINE Pfadwahl in v0 — resolveMerge ist der
 * Erweiterungspunkt fuer die spaetere Pfad-Auswahl (A6).
 * Waffen: Tier-Craft schaltet das Tier im Shop frei.
 */
import type { OwnedChampion, RunState, Tier } from './types'
import { absorbFreedWeapons } from './roster'

export interface MergeOption {
  kind: 'champion' | 'weapon'
  key: string
  tier: Tier // Ausgangs-Tier
  uids: number[] // die 3 zu verschmelzenden Instanzen (deterministisch: kleinste uids)
}

export interface MergeResult {
  kind: 'champion' | 'weapon'
  key: string
  newTier: Tier
  // Erweiterungspunkt A6: spaeter { pathChoices: [...] } fuer die Pfadwahl
}

/** alle aktuell moeglichen Merges (3x gleicher Key + gleiches Tier) */
export function findMerges(state: RunState): MergeOption[] {
  const cfg = state.config
  const out: MergeOption[] = []
  const champGroups = new Map<string, number[]>()
  for (const c of [...state.champions].sort((a, b) => a.uid - b.uid)) {
    if (c.tier >= cfg.upgrade.charMaxTier) continue
    const k = `${c.champ}#${c.tier}`
    const arr = champGroups.get(k) ?? []
    arr.push(c.uid)
    champGroups.set(k, arr)
  }
  for (const [k, uids] of champGroups) {
    if (uids.length >= cfg.upgrade.mergeCount) {
      const [champ, tier] = k.split('#') as [string, string]
      out.push({ kind: 'champion', key: champ, tier: Number(tier) as Tier, uids: uids.slice(0, cfg.upgrade.mergeCount) })
    }
  }
  const weaponGroups = new Map<string, number[]>()
  for (const w of [...state.weapons].sort((a, b) => a.uid - b.uid)) {
    if (w.tier >= cfg.upgrade.weaponMaxTier) continue
    const k = `${w.weapon}#${w.tier}`
    const arr = weaponGroups.get(k) ?? []
    arr.push(w.uid)
    weaponGroups.set(k, arr)
  }
  for (const [k, uids] of weaponGroups) {
    if (uids.length >= cfg.upgrade.mergeCount) {
      const [weapon, tier] = k.split('#') as [string, string]
      out.push({ kind: 'weapon', key: weapon, tier: Number(tier) as Tier, uids: uids.slice(0, cfg.upgrade.mergeCount) })
    }
  }
  return out
}

/** A6-Erweiterungspunkt: bestimmt was aus einem Merge entsteht (v0: nur Tier+1) */
export function resolveMerge(option: MergeOption): MergeResult {
  return { kind: option.kind, key: option.key, newTier: (option.tier + 1) as Tier }
}

/**
 * Merge ausfuehren (manuell, docs/02). Champions: die verschmolzene Unit uebernimmt
 * Position + Waffen der board-platzierten Instanz (sonst Bench); ueberzaehlige Waffen
 * -> Storage, bei vollem Storage mitverkauft (A-CC17).
 */
export function executeMerge(state: RunState, option: MergeOption): boolean {
  const result = resolveMerge(option)
  if (option.kind === 'champion') {
    const parts = option.uids
      .map((uid) => state.champions.find((c) => c.uid === uid))
      .filter((c): c is OwnedChampion => Boolean(c))
    if (parts.length !== option.uids.length) return false
    // survivor: board-platzierte Instanz bevorzugt (kleinste uid), sonst kleinste uid
    const survivor = parts.find((c) => c.hex !== null) ?? parts[0]!
    const others = parts.filter((c) => c !== survivor)
    // Waffen der anderen freigeben (unequip -> Storage; nur Overflow wird verkauft, A-CC17)
    const freed: number[] = []
    for (const o of others) {
      freed.push(...o.weaponUids)
      o.weaponUids = []
    }
    state.champions = state.champions.filter((c) => !others.includes(c))
    absorbFreedWeapons(state, freed)
    survivor.tier = result.newTier
    // Kaufpreis kumulieren, damit der Verkaufswert das investierte Gold grob spiegelt
    survivor.buyPrice = parts.reduce((sum, p) => sum + p.buyPrice, 0)
    survivor.boughtRound = state.round
    return true
  }
  // weapon merge
  const parts = option.uids
    .map((uid) => state.weapons.find((w) => w.uid === uid))
    .filter((w): w is NonNullable<typeof w> => Boolean(w))
  if (parts.length !== option.uids.length) return false
  const survivor = parts[0]!
  const others = parts.slice(1)
  const otherUids = new Set(others.map((w) => w.uid))
  // equipped-Referenzen der verschmolzenen Waffen entfernen
  for (const c of state.champions) {
    c.weaponUids = c.weaponUids.filter((u) => !otherUids.has(u))
  }
  state.weapons = state.weapons.filter((w) => !otherUids.has(w.uid))
  survivor.tier = result.newTier
  survivor.buyPrice = parts.reduce((sum, p) => sum + p.buyPrice, 0)
  survivor.boughtRound = state.round
  // Shop-Unlock: Tier erscheint kuenftig in Ziehungen (docs/02)
  const prev = state.craftedWeaponTiers[option.key] ?? 1
  state.craftedWeaponTiers[option.key] = Math.max(prev, result.newTier)
  return true
}
